// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import { CartModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartLineContainer, { CartLineContainerStyles } from "@steiner/App/Components/Cart/CartLineContainer";
import LinesHeader, { LinesHeaderStyles } from "@steiner/App/Components/Cart/LinesHeader";
import TableCounter from "@steiner/App/Components/Common/TableCounter";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import CartLineNotes from "@steiner/Overrides/Widgets/Cart/CartLineNotes";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    openBidDetailsState: state.xOpenBidDetails,
    allowMultipleWishLists: getSettingsCollection(state).wishListSettings.allowMultipleWishLists,
});

const mapDispatchToProps = {
    addToWishList,
    setAddToListModalIsOpen,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OpenBidsDetailsProductListStyles {
    mainWrapper?: InjectableCss;
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    uomValue?: TypographyPresentationProps;
    cartLineContainerStyles?: CartLineContainerStyles;
    pricingContainer?: InjectableCss;
    buttonContainer?: InjectableCss;
    linesHeader?: LinesHeaderStyles;
    priceStyles?: SmallHeadingAndTextStyles;
    extPriceStyles?: SmallHeadingAndTextStyles;
    addToListLink?: LinkPresentationProps;
}

export const openBidsDetailsProductListStyles: OpenBidsDetailsProductListStyles = {};

const styles = openBidsDetailsProductListStyles;

const OpenBidsDetailsProductList = ({
    openBidDetailsState,
    allowMultipleWishLists,
    addToWishList,
    setAddToListModalIsOpen,
}: Props) => {
    const order = openBidDetailsState.dataView?.salesOrder;

    if (!order) {
        return null;
    }
    const toasterContext = useContext(ToasterContext);

    const newList = order.lineItemList.lineItem.map((line: any) => {
        let strComment = "";
        if (line.lineItemCommentList && line.lineItemCommentList.lineItemComment) {
            strComment = line.lineItemCommentList.lineItemComment.map((note: any) => note.text).join('\n\r');
        }
        return {
            altText: line.partIdentifiers.imageAltText,
            canAddToWishList: line.partIdentifiers.canAddToWishList,
            id: line.id,
            pricing: line.lineItemPrice,
            productId: line.partIdentifiers.productId,
            productUri: line.partIdentifiers.productUri,
            qtyOrdered: line.qtyOrdered.quantity.text,
            qtyShipped: line.qtyShipped.quantity.text,
            shortDescription: line.partIdentifiers.description,
            smallImagePath: line.partIdentifiers.mediumImagePath,
            productNumber: line.partIdentifiers.eclipsePartNumber,
            customerProductNumber: line.partIdentifiers.customerPartNumberList.customerPartNumber,
            manufacturerItem: line.partIdentifiers.mfgNumber,
            price: line.lineItemPrice.unitPriceDisplay,
            selectedUOM: line.qtyOrdered.quantity.uom,
            extendedPrice: line.lineItemPrice.extendedPriceDisplay,
            strComment,
        };
    });

    const addToListClickHandler = (line: any) => {
        const productInfo = {
            productId: line.productId,
            qtyOrdered: line.qtyOrdered,
            unitOfMeasure: line.selectedUOM,
        };

        if (!allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    // XNG-Changes: Update Structure
    return (
        <StyledWrapper {...styles.mainWrapper}>
            <TableCounter
                watchResize={true}
                totalItemsCount={newList.length}
                accordionOnSmallScreen={false}
                headingTitle={translate("Bid Lines")}
            >
                <LinesHeader rightTitles={["", "Price", "Qty", "Ext. Price"]} extendedStyles={styles.linesHeader} />
                {newList.map((line: any) => {
                    return (
                        <CartLineContainer key={line.id} extendedStyles={styles.cartLineContainerStyles}>
                            <CartLineContainer.Image>
                                <ProductImage product={line} extendedStyles={styles.productImage} />
                            </CartLineContainer.Image>
                            <CartLineContainer.Description>
                                <ProductDescription product={line} extendedStyles={styles.productDescription} />
                                <ProductPartNumbers
                                    productNumber={line.productNumber}
                                    customerProductNumber={line.customerProductNumber}
                                    manufacturerItem={line.manufacturerItem}
                                    extendedStyles={styles.productPartNumbers}
                                />
                            </CartLineContainer.Description>
                            <CartLineContainer.Qty>
                                <SmallHeadingAndText
                                    heading={translate("Ordered")}
                                    data-test-selector="qty_Ordered"
                                    text={line.qtyOrdered}
                                />
                                <SmallHeadingAndText heading={translate("Shipped")} text={line.qtyShipped} />
                            </CartLineContainer.Qty>
                            <CartLineContainer.Price>
                                <StyledWrapper {...styles.pricingContainer}>
                                    <SmallHeadingAndText
                                        heading={translate("Price")}
                                        text={line.price}
                                        extendedStyles={styles.priceStyles}
                                    />
                                    <Typography {...styles.uomValue}>{`/${line.selectedUOM}`}</Typography>
                                </StyledWrapper>
                            </CartLineContainer.Price>
                            <CartLineContainer.Total>
                                <SmallHeadingAndText
                                    heading={translate("Ext. Price")}
                                    text={line.extendedPrice}
                                    extendedStyles={styles.extPriceStyles}
                                />
                            </CartLineContainer.Total>
                            <CartLineContainer.Notes>
                                {/* XNG-Changes: classic - addToProject */}
                                <CartLineNotes
                                    cart={{ canModifyOrder: false } as CartModel}
                                    overrideNotes={line?.strComment}
                                    editable={false}
                                    showAddToProjectsLink={true}
                                    addToProjectsClickHandler={addToListClickHandler as any}
                                />
                            </CartLineContainer.Notes>
                        </CartLineContainer>
                    );
                })}
            </TableCounter>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OpenBidsDetailsProductList),
    definition: {
        displayName: "Open Bids Details Product List",
        allowedContexts: ["OpenBidsDetailsPage"],
        group: XNGAGE_ELEMENTS,
    },
};

export default widgetModule;
